import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { TpiTourKey } from '@typeRoots/common/tourProgress';
import { useGetTourProgressQuery, useUpdateTourProgressMutation } from '../api/tourApi';
import { setTourProgress, optimisticMarkComplete, rollbackTourComplete } from '../slice/tourSlice';
import { logger } from 'src/helpers/logger';
import { createTourError, handleTourError } from '../tourErrorHandler';
import { TOUR_ENABLED, TOUR_TIMING } from '../tourConfig';

/**
 * Tour Progress Hook
 * Provides type-safe access to tour completion state and update functions
 *
 * @example
 * const { tourData, isCompleted, markCompleted, isLoading } = useTourProgress();
 *
 * if (!isCompleted('companyLanding')) {
 *   // Show tour
 * }
 *
 * await markCompleted('companyLanding');
 */
export function useTourProgress() {
  const dispatch = useDispatch();

  // Fetch tour progress from backend (skip when tour feature is disabled)
  const { data, isLoading, error } = useGetTourProgressQuery(undefined, { skip: !TOUR_ENABLED });

  // Mutation for updating tour progress
  const [updateTourMutation, { isLoading: isUpdating }] = useUpdateTourProgressMutation();

  // Sync backend data to Redux store when loaded
  useEffect(() => {
    if (data?.tourData) {
      dispatch(setTourProgress(data.tourData));
    }
  }, [data, dispatch]);

  /**
   * Check if a specific tour has been completed
   */
  const isCompleted = useCallback(
    (tourKey: TpiTourKey): boolean => {
      return data?.tourData?.[tourKey] ?? false;
    },
    [data],
  );

  /**
   * Mark a tour as completed with retry logic
   * Updates backend and optimistically updates Redux state
   * Retries with exponential backoff on failure
   *
   * @param tourKey - The tour to mark as completed
   * @param retryCount - Current retry attempt (internal use)
   */
  const markCompleted = useCallback(
    async (tourKey: TpiTourKey, retryCount = 0): Promise<void> => {
      try {
        // Optimistically update Redux state
        dispatch(optimisticMarkComplete(tourKey));

        // Update backend (delta only)
        await updateTourMutation({
          tourData: { [tourKey]: true },
        }).unwrap();

        logger.debug(`Tour "${tourKey}" completed and saved`);
      } catch (error) {
        // Rollback optimistic update on failure
        dispatch(rollbackTourComplete(tourKey));

        // Retry with exponential backoff if attempts remaining
        if (retryCount < TOUR_TIMING.MAX_RETRY_ATTEMPTS) {
          const delay = TOUR_TIMING.RETRY_DELAY_BASE_MS * Math.pow(2, retryCount);
          logger.warn(
            `Tour save failed, retrying in ${delay}ms (attempt ${retryCount + 1}/${TOUR_TIMING.MAX_RETRY_ATTEMPTS})`,
          );

          setTimeout(() => {
            markCompleted(tourKey, retryCount + 1);
          }, delay);
          return;
        }

        // All retries exhausted - log error and notify user
        const tourError = createTourError('TOUR_RETRY_EXHAUSTED', tourKey, error);
        handleTourError(tourError);

        // TODO: Integrate with your Toast component system
        // For now, error is logged. In production, show user-friendly notification
        logger.error('Tour save failed after retries:', tourError.userMessage);
      }
    },
    [dispatch, updateTourMutation],
  );

  return {
    tourData: data?.tourData || null,
    isCompleted,
    markCompleted,
    isLoading: isLoading || isUpdating,
    error,
  };
}
